import ActionTypes from './../common/ActionTypes';
import Strings from './../common/Strings';
import { uiActions, notificationActions } from './index';
import { getFreshUser } from '../helpers';
import Analytics from '../analytics/Analytics';
import Events from '../analytics/Events';

const firebase = require('firebase/app');
require('firebase/auth');
require('firebase/firestore');
require('firebase/functions');

function setLoading(isUserLoading) {
  return {
    type: ActionTypes.USER_LOADING,
    isUserLoading,
  };
}

function setError(error) {
  return {
    type: ActionTypes.USER_ERROR,
    error,
  };
}

function userLoggedIn(user) {
  return {
    type: ActionTypes.USER_LOGGED_IN,
    user,
  };
}

function settingsUpdated(user) {
  return {
    type: ActionTypes.USER_SETTINGS_UPDATED,
    user,
  };
}

function setDeletingAccount(isDeletingAccount) {
  return {
    type: ActionTypes.DELETING_ACCOUNT,
    isDeletingAccount,
  };
}

function setDeleteAccountError(error) {
  return {
    type: ActionTypes.DELETE_ACCOUNT_ERROR,
    error,
  };
}

function setAcceptingInvitation(isAcceptingInvitation) {
  return {
    type: ActionTypes.ACCEPTING_INVITATION,
    isAcceptingInvitation,
  };
}

function getLoginErrorText(code) {
  switch (code) {
    case 'auth/invalid-email':
      return Strings.ERROR.loginInvalidEmail;
    case 'auth/user-disabled':
      return Strings.ERROR.loginInvalidEmail;
    case 'auth/user-not-found':
      return Strings.ERROR.loginUserNotFound;
    case 'auth/wrong-password':
      return Strings.ERROR.loginWrongPassword;
    case 'auth/too-many-requests':
      return Strings.ERROR.tooManyAttempts;
    case 'auth/weak-password':
      return Strings.ERROR.weakPassword;
    case 'auth/email-already-in-use':
      return Strings.ERROR.emailAlreadyInUse;
    case 'auth/network-error':
    case 'auth/unknown':
      return Strings.ERROR.noConnection;
    default:
      return Strings.ERROR.defaultBody;
  }
}

function createUser() {
  return dispatch => {
    const { currentUser } = firebase.auth();

    if (currentUser) {
      const user = getFreshUser(currentUser);

      firebase
        .firestore()
        .collection('users')
        .doc(user.uid)
        .set(user, { merge: true })
        .then(() => {
          dispatch(userLoggedIn(user));
          dispatch(setLoading(false));
          dispatch(notificationActions.scheduleNotifications(user));
          dispatch(uiActions.initialized(true));

          Analytics.onLoggedIn(user);
        })
        .catch(() => {
          dispatch(
            uiActions.showError(
              Strings.ERROR.defaultTitle,
              Strings.ERROR.createUser,
            ),
          );
        });
    }
  };
}

function fetchUser() {
  return dispatch => {
    const { currentUser } = firebase.auth();

    if (currentUser) {
      firebase
        .firestore()
        .collection('users')
        .doc(currentUser.uid)
        .get()
        .then(doc => {
          if (doc.exists) {
            const user = doc.data();
            dispatch(userLoggedIn(user));
            dispatch(setLoading(false));
            dispatch(notificationActions.scheduleNotifications(user));
            dispatch(uiActions.initialized(true));

            Analytics.onLoggedIn(user);
          } else {
            dispatch(createUser());
          }
        })
        .catch(() => {
          dispatch(
            uiActions.showError(
              Strings.ERROR.defaultTitle,
              Strings.ERROR.defaultBody,
            ),
          );
        });
    }
  };
}

function login(email, password) {
  return dispatch => {
    if (email && password) {
      dispatch(setError(''));
      dispatch(setLoading(true));
      firebase
        .auth()
        .signInWithEmailAndPassword(email, password)
        .then(() => {
          setTimeout(() => {
            Analytics.logEvent(Events.LOGIN, { email });
          }, 5000);
        })
        .catch(error => {
          dispatch(setLoading(false));
          dispatch(setError(getLoginErrorText(error.code)));
        });
    } else {
      dispatch(setError(Strings.ERROR.loginValidation));
    }
  };
}

function signUp(email, password) {
  return dispatch => {
    if (email && password) {
      dispatch(setError(''));
      dispatch(setLoading(true));
      firebase
        .auth()
        .createUserAndRetrieveDataWithEmailAndPassword(email, password)
        .then(() => {
          setTimeout(() => {
            Analytics.logEvent(Events.SIGN_UP, { email });
          }, 5000);
        })
        .catch(error => {
          dispatch(setLoading(false));
          dispatch(setError(getLoginErrorText(error.code)));
        });
    } else {
      dispatch(setError(Strings.ERROR.loginValidation));
    }
  };
}

function forgotPassword(email) {
  return dispatch => {
    if (email) {
      dispatch(setError(''));
      dispatch(setLoading(true));
      firebase
        .auth()
        .sendPasswordResetEmail(email)
        .then(() => {
          dispatch(setLoading(false));
        })
        .catch(error => {
          dispatch(setLoading(false));
          dispatch(setError(getLoginErrorText(error.code)));
        });
    } else {
      dispatch(setError(Strings.ERROR.forgotPasswordEmailEmpty));
    }
  };
}

function signOut() {
  Analytics.logEvent(Events.LOG_OUT);

  return dispatch => {
    dispatch(setLoading(true));
    firebase
      .auth()
      .signOut()
      .then(() => {
        dispatch(setLoading(false));
      })
      .catch(() => {
        dispatch(setLoading(false));
        dispatch(
          uiActions.showError(
            Strings.ERROR.defaultTitle,
            Strings.ERROR.defaultBody,
          ),
        );
      });
  };
}

function updateSettings(user, nameChanged) {
  return dispatch => {
    const { currentUser } = firebase.auth();

    if (currentUser) {
      dispatch(setLoading(true));

      Analytics.logEvent(Events.SETTINGS_UPDATE, {
        settings: JSON.stringify(user.settings),
      });

      if (nameChanged) {
        currentUser
          .updateProfile(user)
          .then(() => {
            dispatch(updateProfile(currentUser, user));

            Analytics.onLoggedIn(user);
          })
          .catch(() => {
            dispatch(setLoading(false));
            dispatch(
              uiActions.showError(
                Strings.ERROR.defaultTitle,
                Strings.ERROR.defaultBody,
              ),
            );
          });
      } else {
        dispatch(updateProfile(currentUser, user));
      }
    }
  };
}

function updateProfile(currentUser, user) {
  return dispatch => {
    const firestore = firebase.firestore();
    const userRef = firestore.collection('users').doc(currentUser.uid);

    firestore
      .runTransaction(async transaction => {
        transaction.set(userRef, user, { merge: true });
      })
      .then(async () => {
        const updatedUser = await userRef.get();
        const data = updatedUser.data();
        dispatch(setLoading(false));
        dispatch(settingsUpdated(data));
        dispatch(notificationActions.scheduleNotifications(data));
      })
      .catch(() => {
        dispatch(setLoading(false));
        dispatch(
          uiActions.showError(
            Strings.ERROR.defaultTitle,
            Strings.ERROR.defaultBody,
          ),
        );
      });
  };
}

function deleteAccount(password) {
  return dispatch => {
    dispatch(setDeletingAccount(true));
    dispatch(setDeleteAccountError(''));

    const firestore = firebase.firestore();
    const userId = firebase.auth().currentUser.uid;
    const email = firebase.auth().currentUser.email;

    const userRef = firestore.collection('users').doc(userId);
    const projectsQuery = firestore
      .collection('projects')
      .where('ownerUid', '==', userId);
    const workLogsQuery = firestore
      .collection('workLogs')
      .where('ownerUid', '==', userId);

    const provider = firebase.auth.EmailAuthProvider;
    const credential = provider.credential(
      firebase.auth().currentUser.email,
      password,
    );

    Analytics.logEvent(Events.DELETE_ACCOUNT, { email });

    firebase
      .auth()
      .currentUser.reauthenticateAndRetrieveDataWithCredential(credential)
      .then(async () => {
        // First let's ensure all work logs have no tie to company project before they are deleted
        const batch = firestore.batch();
        const workLogs = await workLogsQuery.get();
        workLogs.forEach(doc => {
          batch.set(doc.ref, { companyProjectId: '' }, { merge: true });
        });

        batch
          .commit()
          .then(() => {
            firestore
              .runTransaction(async transaction => {
                transaction.delete(userRef);

                const projects = await projectsQuery.get();
                projects.forEach(doc => {
                  transaction.delete(doc.ref);
                });

                workLogs.forEach(doc => {
                  transaction.delete(doc.ref);
                });
              })
              .then(() => {
                firebase
                  .auth()
                  .currentUser.delete()
                  .then(() => {
                    dispatch(setDeletingAccount(false));
                    dispatch(setDeleteAccountError(''));
                  })
                  .catch(() => {
                    dispatch(setDeletingAccount(false));
                    dispatch(
                      setDeleteAccountError(Strings.ERROR.deleteAccount),
                    );
                  });
              })
              .catch(() => {
                dispatch(setDeletingAccount(false));
                dispatch(setDeleteAccountError(Strings.ERROR.deleteAccount));
              });
          })
          .catch(() => {
            dispatch(setDeletingAccount(false));
            dispatch(setDeleteAccountError(Strings.ERROR.deleteAccount));
          });
      })
      .catch(error => {
        dispatch(setDeletingAccount(false));
        dispatch(setDeleteAccountError(getLoginErrorText(error.code)));
      });
  };
}

function resetDeleteAccountError() {
  return dispatch => {
    dispatch(setDeleteAccountError(''));
  };
}

function acceptInvitation(acceptInvitationToken, projectId) {
  return dispatch => {
    const { token } = acceptInvitationToken;
    dispatch(setAcceptingInvitation(true));

    firebase
      .auth()
      .currentUser.getIdToken()
      .then(idToken => {
        const httpsCallable = firebase
          .functions()
          .httpsCallable('acceptInvitation');
        httpsCallable(JSON.stringify({ idToken, token, projectId }))
          .then(() => {
            dispatch(setAcceptingInvitation(false));
          })
          .catch(e => {
            dispatch(setAcceptingInvitation(false));
            if (e.code === 'not-found') {
              dispatch(
                uiActions.showError(
                  Strings.ERROR.invitationTokenNotFoundTitle,
                  Strings.ERROR.invitationTokenNotFound,
                ),
              );
            } else {
              dispatch(
                uiActions.showError(
                  Strings.ERROR.defaultTitle,
                  Strings.ERROR.defaultBody,
                ),
              );
            }
          });
      })
      .catch(() => {
        dispatch(setAcceptingInvitation(false));
        dispatch(
          uiActions.showError(
            Strings.ERROR.defaultTitle,
            Strings.ERROR.defaultBody,
          ),
        );
      });
  };
}

export const userActions = {
  login,
  signOut,
  signUp,
  forgotPassword,
  fetchUser,
  updateSettings,
  deleteAccount,
  resetDeleteAccountError,
  settingsUpdated,
  acceptInvitation,
};



// WEBPACK FOOTER //
// ./src/actions/user.js